/** @jsxImportSource @emotion/react */
import { EmailOutlined, PhoneOutlined } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { Section } from '@app/components/Guide/ui/Section'
import { homeContactsAtom, homeClientContactAtom } from '@app/state/HomeAtoms'
import { useAtomValue } from 'jotai'
import type { Contact } from '@app/types'
import { PageTab } from '@app/components/Guide/ui/PageTab'
import { clientContactStyle, navigatorsTable } from './HomeContacts.style'
import { getFullName, getImageSource } from '@app/utils/contacts.utils'

const CONTACT_TYPES = {
  advisor: {
    title: 'YOUR ADVISOR',
    description: `Your advisor is your primary contact. They help connect Resilience Explorer® insights to your organisation's goals, guide onboarding and updates, and ensure the platform continues to meet your needs.`,
  },
  analyst: {
    title: 'YOUR ANALYST',
    description: `Your analyst is your technical specialist. They manage your data, ensure your hazard and asset information is correctly integrated into Resilience Explorer®, and support you with detailed analysis.`,
  },
  principal: {
    title: 'YOUR PRINCIPAL',
    description: `Your principal provides senior technical oversight. They offer expert guidance on risk and adaptation and support the overall direction of your work with Resilience Explorer®.`,
  },
  contract: {
    title: 'YOUR CONTRACT MANAGER',
    description: ` Your contract manager manages the contractual and administrative aspects of your subscription.`,
  },
}

const ContactCard = ({
  contact,
  contactType,
}: {
  contact: Contact
  contactType: keyof typeof CONTACT_TYPES
}) => {
  const { title, description } = CONTACT_TYPES[contactType]

  return (
    <Box className="contact">
      <img src={getImageSource(contact)} alt="Person" />
      <Box className="content">
        <h3>{title}</h3>
        <h4>{getFullName(contact)}</h4>
        {contact.phone && (
          <Box className="contact-info">
            <PhoneOutlined />
            {contact.phone}
          </Box>
        )}
        <Box className="contact-info">
          <EmailOutlined />
          <a href={`mailto:${contact.email}`}>{contact.email}</a>
        </Box>
        <Box className="intro">{description}</Box>
      </Box>
    </Box>
  )
}

const HomeContacts = () => {
  const theme = useTheme()
  const clientContact = useAtomValue(homeClientContactAtom)
  const contacts = useAtomValue(homeContactsAtom)

  return (
    <PageTab
      header="Contacts"
      subheader="As a Resilience Explorer® subscriber, you have access to a dedicated team of specialists who support your adaptation journey. Your Resilience Navigators will work closely with you to ensure you get the most value from the platform and help address your specific risk and resilience challenges."
      tabs={[]}
    >
      {clientContact && (
        <Section id="welcometab-internal">
          <h2>Internal contact</h2>
          To streamline communication and ensure efficient collaboration,{' '}
          {getFullName(clientContact[0])} is your organisation&apos;s primary point of contact for
          the Resilience Explorer® implementation.
          <div css={clientContactStyle}>
            <h4>Internal contact name</h4>
            <p>{getFullName(clientContact[0])}</p>
            {clientContact[0].phone && (
              <p>
                <a href={'tel:' + clientContact[0].phone}>
                  <PhoneOutlined /> <span>{clientContact[0].phone}</span>
                </a>
              </p>
            )}
            {clientContact[0].email && (
              <p>
                <a href={'mailto:' + clientContact[0].email}>
                  <EmailOutlined /> <span>{clientContact[0].email}</span>
                </a>
              </p>
            )}
          </div>
          <br />
          <p>
            If you need to update your designated point of contact or add additional team members to
            our regular communications, please let your Advisor know (details below).
          </p>
        </Section>
      )}
      {contacts && (
        <Section id="welcometab-navigators" backgroundColor="#EDF2F4">
          <h2>Resilience Navigators</h2>
          <Box css={navigatorsTable(theme)}>
            {Object.entries(CONTACT_TYPES).map(([contactType]) => {
              const contact = contacts?.[contactType as keyof typeof CONTACT_TYPES]
              return contact ? (
                <ContactCard
                  key={contactType}
                  contact={contact}
                  contactType={contactType as keyof typeof CONTACT_TYPES}
                />
              ) : null
            })}
          </Box>
        </Section>
      )}
    </PageTab>
  )
}

export default HomeContacts
